import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { Box, Modal } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CommonModalButton from '../../../form-components/CommonModalButton';
import AnimationPage from '../../../pages/AnimationPage';
import SalarySettingBasic from './SalarySettingBasic';
import SalarySettingLesson from './SalarySettingLesson';
import SalarySettingMonthly from './SalarySettingMonthly';
import { HourlyBasicSection, HourlyAllowanceSection } from './SalarySettingHourly';
import {
  SalarySettingOverflow,
  SalarySettingPerformance,
  SalarySettingTaxExempt,
} from './SalarySettingBonus';
import {
  getSalarySetting,
  saveSalarySetting,
  getSalaryPeriod,
  saveSalaryPeriod,
  getSalaryEmployees,
} from '../../../api/salarySetting';
import {
  PageWrapper,
  LeftPanel,
  RightPanel,
  Card,
  ContentCard,
  SectionTitle,
  SearchField,
  ToggleRow,
  ToggleLabel,
  ToggleSwitch,
  PositionGroupItem,
  PositionGroupTitle,
  EmployeeItem,
  EmployeeAvatar,
  EmployeeName,
  EmployeePhone,
  StatusBadge,
  RetiredTag,
  PeriodRow,
  PeriodLabel,
  PeriodNote,
  SaveBadge,
  RealSelect,
  ProfileCard,
  ProfileAvatar,
  ProfileName,
  ProfileStatus,
  ProfileInfoRow,
  ProfileInfoItem,
  ProfileInfoText,
  SalaryTypeCard,
  SalaryTypeIcon,
  SalaryTypeName,
  SalaryTypeDesc,
  SalaryTypeDescItem,
  DotSeparator,
  RadioCircle,
  RadioDot,
  InputBoxText,
  FooterBar,
  CancelButton,
  SubmitButton,
  GuideButton,
  GuideModalContainer,
  GuideModalHeader,
  GuideModalTitle,
  GuideModalBody,
  GuideSection,
  GuideSectionLabel,
  GuideText,
  GuideDivider,
  GuideStepBox,
  GuideStepNumber,
  GuideStepText,
} from './SalarySettingStyled';
import { Close } from '@mui/icons-material';

const SALARY_TYPE_LABELS = { COUNT: '횟수제', MONTHLY: '월급제', HOURLY: '시간제' };

// 초성 검색 유틸
const CHOSUNG = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const getChosung = str =>
  [...(str || '')].map(c => {
    const code = c.charCodeAt(0) - 0xAC00;
    return code >= 0 && code <= 11171 ? CHOSUNG[Math.floor(code / 588)] : c;
  }).join('');
const isChosungOnly = str => [...str].every(c => CHOSUNG.includes(c));

const SALARY_TYPES = [
  {
    key: 'COUNT',
    name: '횟수제 (수업 기반)',
    icon: <ShowChartRoundedIcon sx={{ fontSize: 24, color: '#55C982' }} />,
    bgColor: 'rgba(85, 201, 129, 0.16)',
    desc: ['수업 횟수', '×', '회당 가격', '+', '인센티브'],
  },
  {
    key: 'MONTHLY',
    name: '월급제 (고정 급여)',
    icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 24, color: '#3582C0' }} />,
    bgColor: 'rgba(53, 130, 192, 0.16)',
    desc: ['고정 급여', '+', '인센티브'],
  },
  {
    key: 'HOURLY',
    name: '시간제 (시급 기반)',
    icon: <AccessTimeIcon sx={{ fontSize: 24, color: '#FFB659' }} />,
    bgColor: 'rgba(255, 182, 89, 0.16)',
    desc: ['근로 시간', '×', '시급'],
  },
];

// 비과세 항목(식대/교통비/차량유지비) 1건당 월 한도.
// 백엔드 SalarySettlementService.TAX_EXEMPT_ITEM_LIMIT 과 동일한 값으로 유지 필요.
const TAX_EXEMPT_ITEM_LIMIT = 200000;

const DEFAULT_SETTINGS = {
  salaryType: 'COUNT',
  effectiveStartDate: null,
  useHireDateAsStart: 'N',
  baseAmount: 0,
  deductionType: 'TAX_3_3',
  applyMinWage: 'N',
  overtimeEnabled: 'N',
  overtimeRate: '150%',
  overtimeExtra: 0,
  nightWorkEnabled: 'N',
  nightWorkRate: '150%',
  nightWorkExtra: 0,
  holidayWorkEnabled: 'N',
  holidayWorkRate: '150%',
  holidayWorkExtra: 0,
  weeklyHolidayEnabled: 'N',
  weeklyHolidayRate: '150%',
  weeklyHolidayExtra: 0,
  personalBonusEnabled: 'N',
  personalBonusType: 'FIXED',
  personalBonusThreshold: 0,
  personalBonusAmount: 0,
  personalBonusVatIncluded: 'N',
  personalBonusBasis: 'SALE_PRICE',
  personalBonusTargets: '',
  positionBonusEnabled: 'N',
  positionBonusType: 'FIXED',
  positionBonusThreshold: 0,
  positionBonusAmount: 0,
  positionBonusVatIncluded: 'N',
  positionBonusBasis: 'SALE_PRICE',
  positionBonusTargets: '',
  branchBonusEnabled: 'N',
  branchBonusType: 'FIXED',
  branchBonusThreshold: 0,
  branchBonusAmount: 0,
  branchBonusVatIncluded: 'N',
  branchBonusBasis: 'SALE_PRICE',
  branchBonusTargets: '',
  mealAllowance: 0,
  mealAllowanceEnabled: false,
  transportAllowance: 0,
  transportAllowanceEnabled: false,
  vehicleAllowance: 0,
  vehicleAllowanceEnabled: false,
  customDeductions: [],
  bonusRanges: [],
  // 횟수제 전용
  privateLessonBasicEnabled: 'N',
  privateLessonVatIncluded: 'N',
  privateLessonRenewSeparate: 'N',
  privateLessonBasis: 'SALE_PRICE',
  privateLessonNewAmount: 0,
  privateLessonNewUnit: '원',
  privateLessonRenewAmount: 0,
  privateLessonRenewUnit: '원',
  privateLessonAbsence: 'NOT_SETTLED',
  privateLessonAbsenceAmount: 0,
  privateLessonAbsenceUnit: '원',
  privateLessonIndividualEnabled: 'N',
  individualTickets: [],
  groupLessonEnabled: 'N',
  groupLessonCountBasis: 'ATTEND_ONLY',
  privateOverflowEnabled: 'N',
  privateOverflowVatIncluded: 'N',
  privateOverflowBasis: 'AMOUNT',
  privateOverflowCount: 0,
  privateOverflowAmount: 0,
  groupRanges: [],
};

// 급여방식 전환 시 초기화 대상 필드. 새 방식의 화면에서 보이지 않게 된 필드를 그대로 두면
// DB에는 남아있다가 나중에 그 방식으로 되돌아갔을 때 예전 값이 예고 없이 되살아난다.
const COUNT_ONLY_FIELDS = [
  'privateLessonBasicEnabled', 'privateLessonVatIncluded', 'privateLessonRenewSeparate',
  'privateLessonBasis', 'privateLessonNewAmount', 'privateLessonNewUnit',
  'privateLessonRenewAmount', 'privateLessonRenewUnit', 'privateLessonAbsence',
  'privateLessonAbsenceAmount', 'privateLessonAbsenceUnit', 'privateLessonIndividualEnabled',
  'individualTickets', 'groupLessonEnabled', 'groupLessonCountBasis',
  'privateOverflowEnabled', 'privateOverflowVatIncluded', 'privateOverflowBasis',
  'privateOverflowCount', 'privateOverflowAmount', 'groupRanges',
];
// 월급제·시간제 공통 수당 설정 (연장/야간/휴일) — 횟수제에는 이 섹션 자체가 없다.
const OVERTIME_FIELDS = [
  'overtimeEnabled', 'overtimeRate', 'overtimeExtra',
  'nightWorkEnabled', 'nightWorkRate', 'nightWorkExtra',
  'holidayWorkEnabled', 'holidayWorkRate', 'holidayWorkExtra',
];
// 주휴수당은 시간제 화면에만 있다 (월급제는 월 소정근로시간에 이미 포함).
const WEEKLY_HOLIDAY_FIELDS = ['weeklyHolidayEnabled', 'weeklyHolidayRate', 'weeklyHolidayExtra'];
// 성과급 섹션은 횟수제·월급제에만 있고 시간제에는 없다.
const BONUS_FIELDS = [
  'personalBonusEnabled', 'personalBonusType', 'personalBonusThreshold', 'personalBonusAmount',
  'personalBonusVatIncluded', 'personalBonusBasis', 'personalBonusTargets',
  'positionBonusEnabled', 'positionBonusType', 'positionBonusThreshold', 'positionBonusAmount',
  'positionBonusVatIncluded', 'positionBonusBasis', 'positionBonusTargets',
  'branchBonusEnabled', 'branchBonusType', 'branchBonusThreshold', 'branchBonusAmount',
  'branchBonusVatIncluded', 'branchBonusBasis', 'branchBonusTargets', 'bonusRanges',
];

const resetFieldsFor = fields =>
  fields.reduce((acc, key) => {
    acc[key] = DEFAULT_SETTINGS[key];
    return acc;
  }, {});

// ============================================
// 왼쪽 패널 - 직원 목록
// ============================================
const EmployeeListPanel = ({
  positionGroups,
  selectedEmployee,
  onSelectEmployee,
  excludeRetired,
  onExcludeRetiredChange,
  periodSetting,
  onPeriodChange,
  onPeriodSave,
  onGuideOpen,
}) => {
  const { t } = useTranslation();
  const [expandedPositions, setExpandedPositions] = useState({ 0: true });
  const [searchInput, setSearchInput] = useState('');

  const togglePosition = idx => {
    setExpandedPositions(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const trimmedSearch = searchInput.trim().toLowerCase();

  const filterEmployees = employees => {
    if (!trimmedSearch) return employees;
    return employees.filter(emp => {
      const normalize = v => (v || '').toLowerCase().replace(/-/g, '');
      const name = (emp.name || '').toLowerCase();
      const phone = normalize(emp.phone);
      if (name.includes(trimmedSearch) || phone.includes(trimmedSearch)) return true;
      if (isChosungOnly(trimmedSearch) && getChosung(emp.name).includes(trimmedSearch)) return true;
      return false;
    });
  };

  return (
    <>
      <Card sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <SearchField sx={{ width: '100%' }}>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('이름 및 연락처로 검색')}
            style={{
              border: 'none',
              outline: 'none',
              flex: 1,
              fontSize: '16px',
              background: 'transparent',
              color: '#404040',
              fontFamily: 'inherit',
            }}
          />
          <SearchIcon sx={{ fontSize: 22, color: '#999' }} />
        </SearchField>
        <ToggleRow sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ToggleSwitch checked={excludeRetired} onChange={() => onExcludeRetiredChange(!excludeRetired)} />
            <ToggleLabel>{t('퇴사자 제외')}</ToggleLabel>
          </Box>
          <GuideButton onClick={onGuideOpen}>📖 급여 설정 가이드</GuideButton>
        </ToggleRow>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {positionGroups.map((group, idx) => {
            const filteredEmployees = filterEmployees(group.employees);
            const shouldExpand = trimmedSearch ? filteredEmployees.length > 0 : expandedPositions[idx];

            return (
              <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {shouldExpand && filteredEmployees.length > 0 ? (
                  <Box
                    sx={{
                      border: '1px solid #4AB3BC',
                      borderRadius: '16px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => togglePosition(idx)}
                    >
                      <PositionGroupTitle>{group.positionName}</PositionGroupTitle>
                      <KeyboardArrowUpIcon sx={{ fontSize: 25, color: '#999' }} />
                    </Box>

                    {filteredEmployees.map(emp => (
                      <EmployeeItem
                        key={emp.employeeId}
                        isSelected={selectedEmployee?.employeeId === emp.employeeId}
                        onClick={() => onSelectEmployee(emp)}
                      >
                        <EmployeeAvatar src={emp.profileImage || undefined} />
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <EmployeeName>{emp.name}</EmployeeName>
                              {emp.employeeClassification === 'LEFT' && <RetiredTag>(퇴사)</RetiredTag>}
                            </Box>
                            <EmployeePhone>{emp.phone}</EmployeePhone>
                          </Box>
                          {emp.salaryType && (
                            <StatusBadge type="active">
                              {SALARY_TYPE_LABELS[emp.salaryType] || emp.salaryType}
                            </StatusBadge>
                          )}
                        </Box>
                      </EmployeeItem>
                    ))}
                  </Box>
                ) : (
                  <PositionGroupItem onClick={() => togglePosition(idx)}>
                    <PositionGroupTitle>{group.positionName}</PositionGroupTitle>
                    <KeyboardArrowDownIcon sx={{ fontSize: 25, color: '#999' }} />
                  </PositionGroupItem>
                )}
              </Box>
            );
          })}
        </Box>
      </Card>

      {/* 급여산정 기간 */}
      <Card sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionTitle sx={{ fontSize: '24px' }}>{t('급여산정 기간')}</SectionTitle>
          <SaveBadge onClick={onPeriodSave}>{t('저장')}</SaveBadge>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <PeriodRow>
            <PeriodLabel>{t('매월')}</PeriodLabel>
            <RealSelect
              value={periodSetting.startDay}
              onChange={e => onPeriodChange('startDay', Number(e.target.value))}
              style={{ width: '80px' }}
            >
              {Array.from({ length: 31 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </RealSelect>
            <PeriodLabel>{t('일 부터')}</PeriodLabel>
            <RealSelect
              value={periodSetting.endDay}
              onChange={e => onPeriodChange('endDay', Number(e.target.value))}
              style={{ width: '80px' }}
            >
              {Array.from({ length: 31 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </RealSelect>
            <PeriodLabel>{t('일 까지')}</PeriodLabel>
          </PeriodRow>
          <PeriodNote>{t('※ 종료 날짜를 31일로 설정 시 매월 마지막 날짜로 자동 정산됩니다')}</PeriodNote>
        </Box>
      </Card>
    </>
  );
};

// ============================================
// 오른쪽 - 프로필 대시보드
// ============================================
const ProfileDashboard = ({ employee, salaryType, branchName }) => {
  const { t } = useTranslation();

  return (
    <ProfileCard>
      <ProfileAvatar src={employee?.profileImage} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ProfileName>{employee?.name || '-'}</ProfileName>
          {salaryType && (
            <StatusBadge type="active">{SALARY_TYPE_LABELS[salaryType] || salaryType}</StatusBadge>
          )}
          <ProfileStatus>
            {employee?.employeeClassification === 'LEFT' ? t('퇴사') : t('재직중')}
          </ProfileStatus>
        </Box>
        <ProfileInfoRow>
          {branchName && (
            <ProfileInfoItem>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: '#4AB3BC' }} />
              <ProfileInfoText>{branchName}</ProfileInfoText>
            </ProfileInfoItem>
          )}
          {employee?.hireDate && (
            <ProfileInfoItem>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 14, color: '#4AB3BC' }} />
              <ProfileInfoText>{t('입사일')} {employee.hireDate}</ProfileInfoText>
            </ProfileInfoItem>
          )}
          <ProfileInfoItem>
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: '#4AB3BC' }} />
            <ProfileInfoText>{employee?.phone || '-'}</ProfileInfoText>
          </ProfileInfoItem>
          {employee?.email && (
            <ProfileInfoItem>
              <EmailOutlinedIcon sx={{ fontSize: 14, color: '#4AB3BC' }} />
              <ProfileInfoText>{employee.email}</ProfileInfoText>
            </ProfileInfoItem>
          )}
        </ProfileInfoRow>
      </Box>
    </ProfileCard>
  );
};

// ============================================
// 급여 타입 선택
// ============================================
const SalaryTypeSelector = ({ selectedType, onTypeChange }) => {
  return (
    <Box sx={{ display: 'flex', gap: '24px' }}>
      {SALARY_TYPES.map(type => (
        <SalaryTypeCard
          key={type.key}
          isSelected={selectedType === type.key}
          onClick={() => !type.disabled && onTypeChange(type.key)}
          sx={{
            flex: 1,
            ...(type.disabled && { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SalaryTypeIcon bgColor={type.bgColor}>{type.icon}</SalaryTypeIcon>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <SalaryTypeName>{type.name}</SalaryTypeName>
              <SalaryTypeDesc>
                {type.disabled ? (
                  <SalaryTypeDescItem sx={{ color: '#bbb', fontStyle: 'italic' }}>준비중입니다</SalaryTypeDescItem>
                ) : (
                  type.desc.map((item, i) => (
                    <React.Fragment key={i}>
                      {item === '×' || item === '+' ? (
                        <DotSeparator>{item}</DotSeparator>
                      ) : (
                        <SalaryTypeDescItem>{item}</SalaryTypeDescItem>
                      )}
                    </React.Fragment>
                  ))
                )}
              </SalaryTypeDesc>
            </Box>
          </Box>
          <RadioCircle isSelected={selectedType === type.key}>
            {selectedType === type.key && <RadioDot />}
          </RadioCircle>
        </SalaryTypeCard>
      ))}
    </Box>
  );
};

// ============================================
// 메인 컴포넌트
// ============================================
const SalarySetting = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const branchId = user.branch?.branchId;

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [excludeRetired, setExcludeRetired] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [periodSetting, setPeriodSetting] = useState({ startDay: 1, endDay: 31, signedOnlyYn: 'N' });
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [periodSaveModalOpen, setPeriodSaveModalOpen] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationError, setValidationError] = useState(null); // { field, message }
  // 그룹레슨 구간 사이가 비어 있을 때의 경고. 저장을 막지는 않고 확인 모달에서 알려준다.
  const [groupRangeGapWarning, setGroupRangeGapWarning] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);

  // 직원 목록 조회
  const { data: positionGroups = [] } = useQuery({
    queryKey: ['salaryEmployees', branchId, excludeRetired],
    queryFn: () => getSalaryEmployees(branchId, excludeRetired, user.token),
    enabled: !!branchId,
  });

  // 첫 직원 자동 선택
  useEffect(() => {
    if (positionGroups.length > 0 && !selectedEmployee) {
      const firstGroup = positionGroups.find(g => g.employees.length > 0);
      if (firstGroup) {
        setSelectedEmployee(firstGroup.employees[0]);
      }
    }
  }, [positionGroups]);

  // 선택된 직원의 급여 설정 조회 (지점별)
  const currentBranchId = user?.branch?.branchId;
  const [settingsVersion, setSettingsVersion] = useState(0);
  const { data: salaryData } = useQuery({
    queryKey: ['salarySetting', selectedEmployee?.employeeId, currentBranchId],
    queryFn: () => getSalarySetting(selectedEmployee.employeeId, currentBranchId, user.token),
    enabled: !!selectedEmployee?.employeeId && !!currentBranchId,
  });

  // 급여 설정 데이터 → state 반영
  // settingsVersion 은 "서버에서 불러온 설정이 통째로 갈렸다"는 신호다.
  // 이 값을 key 로 쓰는 하위 섹션은 그때 다시 마운트되어, 불러온 값 기준으로 내부 상태를 다시 잡는다.
  useEffect(() => {
    if (salaryData) {
      setSettings({ ...DEFAULT_SETTINGS, ...salaryData });
    } else if (selectedEmployee) {
      setSettings(DEFAULT_SETTINGS);
    }
    setSettingsVersion(v => v + 1);
  }, [salaryData, selectedEmployee?.employeeId]);

  // 급여산정 기간 조회
  const { data: periodData } = useQuery({
    queryKey: ['salaryPeriod', branchId],
    queryFn: () => getSalaryPeriod(branchId, user.token),
    enabled: !!branchId,
  });

  useEffect(() => {
    if (periodData) {
      setPeriodSetting({ startDay: periodData.startDay, endDay: periodData.endDay, signedOnlyYn: periodData.signedOnlyYn || 'N' });
    }
  }, [periodData]);

  // 급여 설정 저장
  const saveMutation = useMutation({
    mutationFn: data => saveSalarySetting(data, user.token),
    onSuccess: () => {
      toast.success(t('급여 설정이 저장되었습니다'));
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['salarySetting'] });
      queryClient.invalidateQueries({ queryKey: ['salaryEmployees'] });
    },
  });

  // 급여산정 기간 저장
  const periodMutation = useMutation({
    mutationFn: data => saveSalaryPeriod(data, user.token),
    onSuccess: () => {
      toast.success(t('급여산정 기간이 저장되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['salaryPeriod'] });
    },
  });

  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
    if (validationError) setValidationError(null);
  };

  // 급여방식 전환. baseAmount 는 한 필드지만 방식마다 뜻이 다르다 —
  // 횟수제·월급제는 "월 기본 급여액", 시간제는 "시급".
  // 그래서 시간제 경계를 넘을 때만 값을 비운다. 안 비우면 횟수제 100만원짜리 직원을
  // 시간제로 바꿨을 때 시급 100만원(월 2억)이 그대로 저장된다.
  // 횟수제 ↔ 월급제는 뜻이 같으므로 값을 유지한다.
  const handleSalaryTypeChange = nextType => {
    const crossesHourly = (settings.salaryType === 'HOURLY') !== (nextType === 'HOURLY');
    const clearedFields = {
      ...(nextType !== 'COUNT' ? resetFieldsFor(COUNT_ONLY_FIELDS) : {}),
      ...(nextType === 'COUNT' ? resetFieldsFor(OVERTIME_FIELDS) : {}),
      ...(nextType !== 'HOURLY' ? resetFieldsFor(WEEKLY_HOLIDAY_FIELDS) : {}),
      ...(nextType === 'HOURLY' ? resetFieldsFor(BONUS_FIELDS) : {}),
    };
    setSettings(prev => ({
      ...prev,
      ...clearedFields,
      salaryType: nextType,
      ...(crossesHourly ? { baseAmount: 0, applyMinWage: 'N' } : {}),
    }));
    setHasUnsavedChanges(true);
    if (validationError) setValidationError(null);
  };

  const handleSelectEmployee = emp => {
    if (hasUnsavedChanges && selectedEmployee) {
      setPendingEmployee(emp);
      setSwitchModalOpen(true);
    } else {
      setSelectedEmployee(emp);
      setHasUnsavedChanges(false);
    }
  };

  const handleSwitchConfirm = () => {
    setSwitchModalOpen(false);
    setSelectedEmployee(pendingEmployee);
    setPendingEmployee(null);
    setHasUnsavedChanges(false);
  };

  const setError = (field, message) => {
    setValidationError({ field, message });
    toast.error(t(message));
    // 해당 필드로 스크롤
    setTimeout(() => {
      const el = document.querySelector(`[data-field="${field}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return false;
  };

  const validateSettings = () => {
    setValidationError(null);

    // 공통 필수
    if (settings.baseAmount == null && settings.baseAmount !== 0) {
      return setError('baseAmount', '기본 급여액을 입력해주세요');
    }
    if (!settings.deductionType) {
      return setError('deductionType', '공제 항목을 선택해주세요');
    }
    if (settings.deductionType === 'CUSTOM') {
      if (!settings.customDeductions || settings.customDeductions.length === 0) {
        return setError('customDeductions', '직접입력 공제 항목을 1개 이상 추가해주세요');
      }
      const emptyIdx = settings.customDeductions.findIndex(d => !d.itemName || !d.rate);
      if (emptyIdx >= 0) {
        return setError(`customDeduction_${emptyIdx}`, '공제 항목명과 비율을 모두 입력해주세요');
      }
    }

    // 횟수제 전용 검증
    if (settings.salaryType === 'COUNT') {
      if (settings.privateLessonBasicEnabled === 'Y') {
        if (!settings.privateLessonNewAmount) {
          return setError('privateLessonNewAmount', '개인레슨 신규 수당을 입력해주세요');
        }
        if (settings.privateLessonRenewSeparate === 'Y') {
          if (!settings.privateLessonRenewAmount) {
            return setError('privateLessonRenewAmount', '재등록 수당 금액을 입력해주세요');
          }
        }
        if (settings.privateLessonAbsence === 'CUSTOM' && !settings.privateLessonAbsenceAmount && settings.privateLessonAbsenceAmount !== 0) {
          return setError('privateLessonAbsenceAmount', '결석 수당 금액을 입력해주세요');
        }
      }
      // 개별 수강권 수당 검증
      if (settings.privateLessonIndividualEnabled === 'Y' && settings.individualTickets?.length > 0) {
        const appliedTickets = settings.individualTickets.filter(t =>
          (t.newAmount || 0) > 0 || (t.renewAmount || 0) > 0
        );
        // 개별 수당 활성화했는데 적용된 수강권이 하나도 없으면
        // → 기본 수당으로 계산되므로 에러 아님 (경고만)
      }

      if (settings.groupLessonEnabled === 'Y') {
        if (!settings.groupRanges || settings.groupRanges.length === 0) {
          return setError('groupRanges', '그룹레슨 구간을 1개 이상 추가해주세요');
        }
        const zeroAmountRange = settings.groupRanges.find(r => !r.amount);
        if (zeroAmountRange) {
          return setError('groupRangeAmount', '그룹레슨 구간 수당을 입력해주세요');
        }

        // 구간 검증 — rangeOrder 순으로 정렬해 인접한 구간끼리만 비교한다.
        // 백엔드는 첫 매칭에서 멈추므로, 겹치면 앞선 구간이 조용히 이긴다. 관리자 의도와
        // 다를 수 있어 저장을 막는다.
        const sortedGroupRanges = [...settings.groupRanges]
          .sort((a, b) => (a.rangeOrder ?? 0) - (b.rangeOrder ?? 0));

        // 구간의 상한을 넘는 인원은 백엔드가 마지막 구간 요율로 정산한다(rangeEnd 는 항상 값을 가진다).
        const reversedRange = sortedGroupRanges.find(
          r => r.rangeEnd != null && (r.rangeStart ?? 0) > r.rangeEnd);
        if (reversedRange) {
          return setError('groupRanges', '그룹레슨 구간 범위가 올바르지 않습니다');
        }

        const overlapIdx = sortedGroupRanges.findIndex(
          (r, i) => i > 0
            && sortedGroupRanges[i - 1].rangeEnd != null
            && (r.rangeStart ?? 0) <= sortedGroupRanges[i - 1].rangeEnd);
        if (overlapIdx > 0) {
          const prev = sortedGroupRanges[overlapIdx - 1];
          return setError('groupRanges',
            `그룹레슨 구간이 겹칩니다 (${prev.rangeStart}~${prev.rangeEnd}명 / ` +
            `${sortedGroupRanges[overlapIdx].rangeStart}~${sortedGroupRanges[overlapIdx].rangeEnd}명)`);
        }

        // 공백은 막지 않는다. 그 인원대에 수당을 주지 않으려는 의도일 수 있다.
        // 다만 비어 있는 구간의 수업은 0원이 되므로 저장 전에 알려는 준다.
        const gapIdx = sortedGroupRanges.findIndex(
          (r, i) => i > 0
            && sortedGroupRanges[i - 1].rangeEnd != null
            && (r.rangeStart ?? 0) > sortedGroupRanges[i - 1].rangeEnd + 1);
        setGroupRangeGapWarning(
          gapIdx > 0
            ? `${sortedGroupRanges[gapIdx - 1].rangeEnd + 1}명 ~ ` +
              `${sortedGroupRanges[gapIdx].rangeStart - 1}명 구간이 비어 있습니다. ` +
              '이 인원의 수업은 수당이 0원으로 계산됩니다.'
            : '');
      }
    }

    // 비과세 토글 ON 시 금액 필수
    if ((settings.mealAllowanceEnabled || settings.mealAllowance > 0) && !settings.mealAllowance) {
      return setError('mealAllowance', '식대 금액을 입력해주세요');
    }
    if ((settings.transportAllowanceEnabled || settings.transportAllowance > 0) && !settings.transportAllowance) {
      return setError('transportAllowance', '교통비 금액을 입력해주세요');
    }
    if ((settings.vehicleAllowanceEnabled || settings.vehicleAllowance > 0) && !settings.vehicleAllowance) {
      return setError('vehicleAllowance', '차량 유지비 금액을 입력해주세요');
    }

    // 비과세 항목별 한도(월 20만원) 초과 검증
    const taxExemptLimitLabel = `월 ${TAX_EXEMPT_ITEM_LIMIT.toLocaleString()}원`;
    if (settings.mealAllowance > TAX_EXEMPT_ITEM_LIMIT) {
      return setError('mealAllowance', `식대는 비과세 한도(${taxExemptLimitLabel}) 이하로 입력해주세요`);
    }
    if (settings.transportAllowance > TAX_EXEMPT_ITEM_LIMIT) {
      return setError('transportAllowance', `교통비는 비과세 한도(${taxExemptLimitLabel}) 이하로 입력해주세요`);
    }
    if (settings.vehicleAllowance > TAX_EXEMPT_ITEM_LIMIT) {
      return setError('vehicleAllowance', `차량 유지비는 비과세 한도(${taxExemptLimitLabel}) 이하로 입력해주세요`);
    }

    // 초과수당 검증 — 횟수제 전용 섹션이므로 횟수제일 때만 본다.
    // 급여방식을 바꾸면 이 섹션이 화면에서 사라지는데, 검증이 급여방식과 무관하게 돌면
    // 화면에 없는 필드 때문에 저장이 막히고 끌 방법도 없어진다(교착).
    if (settings.salaryType === 'COUNT' && settings.privateOverflowEnabled === 'Y') {
      if (!settings.privateOverflowCount) {
        return setError('privateOverflowCount', '개인레슨 초과수당 기준을 입력해주세요');
      }
      if (!settings.privateOverflowAmount) {
        return setError('privateOverflowAmount', '개인레슨 초과수당 금액을 입력해주세요');
      }
    }

    // 성과급 검증 — 성과급 섹션은 횟수제·월급제에만 있다. 시간제는 계산에서도 제외되므로
    // 시간제로 전환한 뒤에는 검증하지 않는다(초과수당과 같은 이유).
    const bonusCategories = settings.salaryType === 'HOURLY' ? [] : [
      { enabled: 'personalBonusEnabled', targets: 'personalBonusTargets', label: '개인 성과급', category: 'PERSONAL' },
      { enabled: 'positionBonusEnabled', targets: 'positionBonusTargets', label: '직급 성과급', category: 'POSITION' },
      { enabled: 'branchBonusEnabled', targets: 'branchBonusTargets', label: '지점 성과급', category: 'BRANCH' },
    ];
    for (const bc of bonusCategories) {
      if (settings[bc.enabled] === 'Y') {
        // 산정 대상을 하나도 안 고르면 백엔드가 0원으로 계산한다 — 성과급을 켰는데 조용히
        // 0원만 나오는 걸 막기 위해 저장 시점에 최소 1개는 고르도록 강제한다.
        if (!settings[bc.targets]) {
          return setError(`${bc.category}_bonusTargets`, `${bc.label} 산정 대상을 하나 이상 선택해주세요`);
        }
        const ranges = (settings.bonusRanges || []).filter(r => r.bonusCategory === bc.category);
        if (ranges.length === 0) {
          return setError(`${bc.category}_bonusRange`, `${bc.label} 구간을 설정해주세요`);
        }
        const emptyRange = ranges.find(r => !r.amount);
        if (emptyRange) {
          return setError(`${bc.category}_bonusRangeAmount`, `${bc.label} 구간 금액을 입력해주세요`);
        }
        // %가 100을 넘으면 매출 전체보다 많이 지급하는 셈이라 정상적인 값일 수 없다.
        // 6%를 입력하려다 6000이 들어가는 실수를 여기서 막는다.
        const invalidPercent = ranges.find(r => r.unit === '%' && r.amount > 100);
        if (invalidPercent) {
          return setError(`${bc.category}_bonusRangePercent`, `${bc.label} 구간 요율은 0~100% 사이로 입력해주세요`);
        }
        // rangeEnd 가 비어 있으면 상한 없음(마지막 구간)이므로 범위 검사에서 제외한다
        const invalidRange = ranges.find(r => r.rangeEnd != null && r.rangeStart > r.rangeEnd);
        if (invalidRange) {
          return setError(`${bc.category}_bonusRangeInvalid`, `${bc.label} 구간 범위가 올바르지 않습니다`);
        }
      }
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!selectedEmployee) return;
    if (!validateSettings()) return;
    setSaveModalOpen(true);
  };

  // 성과급 구간의 마지막 항목은 화면에 상한 입력칸이 없다.
  // 예전에 저장된 설정에는 상한이 남아 있어 기준액이 그 값을 넘으면 성과급이 0원이 되므로,
  // 저장 시점에 카테고리별 마지막 구간의 rangeEnd 를 비워 상한 없음으로 정규화한다.
  const openEndLastBonusRange = ranges => {
    if (!ranges?.length) return ranges;
    const lastOrderByCategory = ranges.reduce((acc, r) => {
      const order = r.rangeOrder ?? 0;
      acc[r.bonusCategory] = Math.max(acc[r.bonusCategory] ?? -1, order);
      return acc;
    }, {});
    return ranges.map(r =>
      (r.rangeOrder ?? 0) === lastOrderByCategory[r.bonusCategory] ? { ...r, rangeEnd: null } : r);
  };

  const handleSaveConfirm = () => {
    setSaveModalOpen(false);
    saveMutation.mutate({
      employeeId: selectedEmployee.employeeId,
      branchId: currentBranchId,
      ...settings,
      bonusRanges: openEndLastBonusRange(settings.bonusRanges),
    });
  };

  const handleCancel = () => {
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = () => {
    setCancelModalOpen(false);
    if (salaryData) {
      setSettings(prev => ({ ...DEFAULT_SETTINGS, ...salaryData }));
    } else {
      setSettings(DEFAULT_SETTINGS);
    }
    setHasUnsavedChanges(false);
    toast.success(t('변경사항이 초기화되었습니다'));
  };

  const handlePeriodSaveClick = () => {
    setPeriodSaveModalOpen(true);
  };

  const handlePeriodSaveConfirm = () => {
    setPeriodSaveModalOpen(false);
    periodMutation.mutate({
      branchId,
      startDay: periodSetting.startDay,
      endDay: periodSetting.endDay,
    });
  };

  return (
    <AnimationPage>
      <PageWrapper>
        {/* 왼쪽 패널 */}
        <LeftPanel>
          <EmployeeListPanel
            positionGroups={positionGroups}
            selectedEmployee={selectedEmployee}
            onSelectEmployee={handleSelectEmployee}
            excludeRetired={excludeRetired}
            onExcludeRetiredChange={setExcludeRetired}
            periodSetting={periodSetting}
            onPeriodChange={(key, val) => setPeriodSetting(prev => ({ ...prev, [key]: val }))}
            onPeriodSave={handlePeriodSaveClick}
            onGuideOpen={() => setGuideOpen(true)}
          />
        </LeftPanel>

        {/* 오른쪽 콘텐츠 */}
        <RightPanel>
          {selectedEmployee ? (
            <>
              <ContentCard>
                <ProfileDashboard employee={selectedEmployee} salaryType={settings.salaryType} branchName={user.branch?.branchName} />

                <SalaryTypeSelector
                  selectedType={settings.salaryType}
                  onTypeChange={handleSalaryTypeChange}
                />

                {settings.salaryType === 'HOURLY' ? (
                  <HourlyBasicSection settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} employee={selectedEmployee} />
                ) : (
                  <SalarySettingBasic settings={settings} onSettingsChange={handleSettingsChange} employee={selectedEmployee} validationError={validationError} />
                )}

                {settings.salaryType === 'COUNT' && (
                  <>
                    <SalarySettingLesson settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} employee={selectedEmployee} />
                    <SalarySettingOverflow settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} />
                  </>
                )}

                {settings.salaryType === 'MONTHLY' && (
                  <SalarySettingMonthly settings={settings} onSettingsChange={handleSettingsChange} />
                )}

                {settings.salaryType === 'HOURLY' && (
                  <HourlyAllowanceSection key={settingsVersion} settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} />
                )}

                {(settings.salaryType === 'COUNT' || settings.salaryType === 'MONTHLY') && (
                  <SalarySettingPerformance settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} />
                )}

                <SalarySettingTaxExempt settings={settings} onSettingsChange={handleSettingsChange} validationError={validationError} />
              </ContentCard>

              <FooterBar>
                <CancelButton onClick={handleCancel}>{t('취소')}</CancelButton>
                <SubmitButton onClick={handleSaveClick} disabled={saveMutation.isPending}>
                  {t('저장')}
                </SubmitButton>
              </FooterBar>
            </>
          ) : (
            <ContentCard>
              <Box sx={{ textAlign: 'center', padding: '60px', color: '#A6A6A6', fontSize: '16px' }}>
                {t('직원을 선택해주세요')}
              </Box>
            </ContentCard>
          )}
        </RightPanel>
      </PageWrapper>

      {/* 저장 확인 모달 */}
      <Modal open={saveModalOpen} onClose={() => setSaveModalOpen(false)} disableScrollLock>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '400px', bgcolor: '#fff', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
          <Box sx={{ fontSize: '22px', fontWeight: 600, color: '#333', marginBottom: '24px' }}>
            급여 설정을 <span style={{ color: '#4AB3BC', fontWeight: 700, fontSize: '26px' }}>저장</span>하시겠습니까?
            {groupRangeGapWarning && (
              <Box sx={{ marginTop: '12px', fontSize: '14px', fontWeight: 500, color: '#CF1322', lineHeight: 1.5 }}>
                {groupRangeGapWarning}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={() => setSaveModalOpen(false)}>{t('닫기')}</CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSaveConfirm}>{t('저장')}</CommonModalButton>
          </Box>
        </Box>
      </Modal>

      {/* 급여산정 기간 저장 확인 모달 */}
      <Modal open={periodSaveModalOpen} onClose={() => setPeriodSaveModalOpen(false)} disableScrollLock>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '400px', bgcolor: '#fff', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
          <Box sx={{ fontSize: '22px', fontWeight: 600, color: '#333', marginBottom: '24px' }}>
            급여산정 기간을 <span style={{ color: '#4AB3BC', fontWeight: 700, fontSize: '26px' }}>저장</span>하시겠습니까?<br />
            <span style={{ fontSize: '14px', color: '#999' }}>변경 시 모든 직원의 정산 기간에 적용됩니다.</span>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={() => setPeriodSaveModalOpen(false)}>{t('닫기')}</CommonModalButton>
            <CommonModalButton variant="submit" onClick={handlePeriodSaveConfirm}>{t('저장')}</CommonModalButton>
          </Box>
        </Box>
      </Modal>

      {/* 직원 전환 확인 모달 */}
      <Modal open={switchModalOpen} onClose={() => setSwitchModalOpen(false)} disableScrollLock>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '400px', bgcolor: '#fff', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
          <Box sx={{ fontSize: '22px', fontWeight: 600, color: '#333', marginBottom: '24px' }}>
            저장하지 않은 변경사항이 있습니다.<br />
            <span style={{ fontSize: '14px', color: '#999' }}>다른 직원을 선택하면 변경사항이 사라집니다.</span>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={() => setSwitchModalOpen(false)}>{t('취소')}</CommonModalButton>
            <CommonModalButton variant="delete" onClick={handleSwitchConfirm}>{t('이동')}</CommonModalButton>
          </Box>
        </Box>
      </Modal>

      {/* 취소 확인 모달 */}
      <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} disableScrollLock>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '400px', bgcolor: '#fff', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
          <Box sx={{ fontSize: '22px', fontWeight: 600, color: '#333', marginBottom: '24px' }}>
            변경사항을 <span style={{ color: '#EE735A', fontWeight: 700 }}>취소</span>하시겠습니까?<br />
            <span style={{ fontSize: '14px', color: '#999' }}>저장하지 않은 변경사항은 사라집니다.</span>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={() => setCancelModalOpen(false)}>{t('닫기')}</CommonModalButton>
            <CommonModalButton variant="delete" onClick={handleCancelConfirm}>{t('확인')}</CommonModalButton>
          </Box>
        </Box>
      </Modal>

      {/* 급여 설정 가이드 */}
      <Modal open={guideOpen} onClose={() => setGuideOpen(false)} disableScrollLock>
        <GuideModalContainer>
          <GuideModalHeader>
            <GuideModalTitle>급여 설정 가이드</GuideModalTitle>
            <Close onClick={() => setGuideOpen(false)} sx={{ color: '#fff', cursor: 'pointer' }} />
          </GuideModalHeader>
          <GuideModalBody>
            <GuideSection>
              <GuideSectionLabel>💰 급여 설정이란?</GuideSectionLabel>
              <GuideText>직원별 급여 계산 방식을 설정합니다. 설정 완료 후 급여 정산 메뉴에서 정산을 생성하고 관리할 수 있습니다.</GuideText>
            </GuideSection>
            <GuideDivider />
            <GuideSection>
              <GuideSectionLabel>📋 횟수제 설정 방법</GuideSectionLabel>
              <GuideStepBox>
                <GuideStepNumber>1</GuideStepNumber>
                <GuideStepText>좌측에서 직원을 선택하고, <strong>횟수제 (수업 기반)</strong> 카드를 클릭합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>2</GuideStepNumber>
                <GuideStepText><strong>급여 기본 설정</strong>에서 적용 시작일, 기본 급여액, 공제 항목을 설정합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>3</GuideStepNumber>
                <GuideStepText><strong>수업 수당 설정</strong>에서 개인레슨/그룹레슨 수당을 설정합니다. 정액(원) 또는 정률(%)로 설정 가능합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>4</GuideStepNumber>
                <GuideStepText>필요 시 <strong>초과수당</strong>, <strong>성과급</strong>, <strong>비과세 항목</strong>을 추가 설정합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>5</GuideStepNumber>
                <GuideStepText><strong>저장</strong> 버튼을 눌러 설정을 완료합니다.</GuideStepText>
              </GuideStepBox>
            </GuideSection>
            <GuideDivider />
            <GuideSection>
              <GuideSectionLabel>✍️ 수업 사인 정산</GuideSectionLabel>
              <GuideText>
                강사가 강사앱에서 수업 사인을 완료한 수업만 정산에 포함하는 기능입니다.<br />
                사인하지 않은 수업은 정산에서 자동으로 제외됩니다.<br /><br />
                설정 경로: <strong>센터 관리</strong> → <strong>센터 목록</strong> → <strong>센터 상세</strong> → <strong>센터설정</strong> → <strong>급여 설정</strong> 탭
              </GuideText>
            </GuideSection>
            <GuideDivider />
            <GuideSection>
              <GuideSectionLabel>📌 설정 후 정산 방법</GuideSectionLabel>
              <GuideText>
                급여 설정 저장 후 <strong>임직원 관리</strong> → <strong>급여 정산</strong> 메뉴로 이동하여 <strong>정산 생성</strong> 버튼으로 정산을 생성합니다.<br />
                수업 이력과 급여 항목을 확인한 뒤 <strong>정산 완료</strong>로 확정하면 급여 명세서가 생성됩니다.
              </GuideText>
            </GuideSection>
          </GuideModalBody>
        </GuideModalContainer>
      </Modal>
    </AnimationPage>
  );
};

export default SalarySetting;
